import { useMemo, useState } from "react";
import { Search, Users, FileText, X } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { SummaryBanner } from "../charts/FinanceiroModalContent";
import { formatCurrency, formatDate } from "@/utils/format";
import { REGUA_LABEL, SPC_LABEL } from "../inadimplenciaUtils";
import type { InadimplenciaClienteRow, InadimplenciaParcela } from "@/services/inadimplencia";

const PER_PAGE = 25;

export interface InadimplenciaDetalhe {
  title: string;
  color: string;
  clientes: InadimplenciaClienteRow[];
  boletos: InadimplenciaParcela[];
  totalLabel: string;
  totalValue: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  detalhe: InadimplenciaDetalhe | null;
  onSelectCliente?: (cliente: InadimplenciaClienteRow) => void;
}

function matchFilter(value: string, filter: string): boolean {
  if (!filter) return true;
  return value.toLowerCase().includes(filter.toLowerCase());
}

function FilterInput({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-300" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-slate-200 bg-white py-1 pl-6 pr-2 text-xs text-slate-700 placeholder:text-slate-300 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
      />
    </div>
  );
}

export function InadimplenciaDetailModal({ open, onClose, detalhe, onSelectCliente }: Props) {
  const [tab, setTab] = useState<"parcelas" | "clientes">("parcelas");
  const [page, setPage] = useState(0);
  const [qCliente, setQCliente] = useState("");
  const [qTitulo, setQTitulo] = useState("");
  const [qVenc, setQVenc] = useState("");
  const [qCond, setQCond] = useState("");

  const boletos = useMemo(() => {
    const list = detalhe?.boletos ?? [];
    return list.filter((b) => {
      return (
        matchFilter(b.clientName, qCliente) &&
        matchFilter(`${b.billId} ${b.installmentNumber ?? ""}`, qTitulo) &&
        matchFilter(b.dueDate ? formatDate(b.dueDate) : "", qVenc) &&
        matchFilter(b.conditionType ?? "", qCond)
      );
    });
  }, [detalhe, qCliente, qTitulo, qVenc, qCond]);

  const clientes = useMemo(() => {
    const list = detalhe?.clientes ?? [];
    return list.filter((c) => matchFilter(c.clientName, qCliente) || matchFilter(c.clientDocument ?? "", qCliente));
  }, [detalhe, qCliente]);

  const rows = tab === "parcelas" ? boletos : clientes;
  const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const safePage = Math.min(page, pages - 1);
  const visible = rows.slice(safePage * PER_PAGE, (safePage + 1) * PER_PAGE);
  const saldoParcelas = boletos.reduce((s, b) => s + b.balanceAmount, 0);
  const hasFilter = Boolean(qCliente || qTitulo || qVenc || qCond);

  function reset() {
    setQCliente("");
    setQTitulo("");
    setQVenc("");
    setQCond("");
    setPage(0);
  }

  function handleClose() {
    reset();
    setTab("parcelas");
    onClose();
  }

  if (!detalhe) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="full"
      closeOnEscape
      closeOnBackdropClick
      title={
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-50">
            <FileText className="h-4 w-4 text-rose-600" />
          </div>
          <span className="text-sm font-semibold leading-tight text-slate-800">{detalhe.title}</span>
        </div>
      }
    >
      <div className="flex h-full flex-col gap-4">
        <SummaryBanner
          label={detalhe.totalLabel}
          value={formatCurrency(detalhe.totalValue)}
          color={detalhe.color}
          subtitle={`${detalhe.clientes.length} cliente(s) · ${detalhe.boletos.length} parcela(s)`}
        />

        <div className="grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-4">
          <FilterInput placeholder="Cliente" value={qCliente} onChange={(v) => { setQCliente(v); setPage(0); }} />
          <FilterInput placeholder="Nº título / parcela" value={qTitulo} onChange={(v) => { setQTitulo(v); setPage(0); }} />
          <FilterInput placeholder="Vencimento" value={qVenc} onChange={(v) => { setQVenc(v); setPage(0); }} />
          <FilterInput placeholder="Condição" value={qCond} onChange={(v) => { setQCond(v); setPage(0); }} />
        </div>

        {hasFilter && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
          >
            <X className="h-3 w-3" />
            Limpar filtros
          </button>
        )}

        <div className="flex shrink-0 gap-4 border-b border-slate-200">
          <button
            type="button"
            onClick={() => { setTab("parcelas"); setPage(0); }}
            className={`-mb-px border-b-2 py-2 text-sm font-medium ${
              tab === "parcelas" ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500"
            }`}
          >
            Parcelas ({boletos.length})
          </button>
          <button
            type="button"
            onClick={() => { setTab("clientes"); setPage(0); }}
            className={`-mb-px border-b-2 py-2 text-sm font-medium ${
              tab === "clientes" ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500"
            }`}
          >
            Clientes ({clientes.length})
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-100">
          {tab === "parcelas" ? (
            <table className="w-full min-w-[960px] border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b-2 border-slate-100">
                  <th className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Título</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Parcela</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Cliente</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Condição</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Vencimento</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Valor</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Saldo</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Atraso</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-sm text-slate-400">
                      Nenhuma parcela neste recorte.
                    </td>
                  </tr>
                ) : (
                  (visible as InadimplenciaParcela[]).map((b) => (
                    <tr key={`${b.billId}-${b.installmentId ?? b.dueDate}`} className="border-b border-slate-50 hover:bg-slate-50/70">
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-slate-600">{b.billId}</td>
                      <td className="px-3 py-2.5">{b.installmentNumber ?? b.installmentId ?? "—"}</td>
                      <td className="max-w-[220px] truncate px-3 py-2.5 font-medium text-slate-800" title={b.clientName}>
                        {b.clientName}
                      </td>
                      <td className="px-3 py-2.5">{b.conditionType || "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2.5">{b.dueDate ? formatDate(b.dueDate) : "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">{formatCurrency(b.originalAmount)}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums">{formatCurrency(b.balanceAmount)}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                        {b.isPaid ? "Pago" : b.daysOverdue > 0 ? `${b.daysOverdue} d` : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[800px] border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b-2 border-slate-100">
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Cliente</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Atraso</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Vencido</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Carteira</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Régua</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">SPC</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Acordo</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-sm text-slate-400">
                      Nenhum cliente neste recorte.
                    </td>
                  </tr>
                ) : (
                  (visible as InadimplenciaClienteRow[]).map((c) => (
                    <tr
                      key={c.clientKey}
                      className={`border-b border-slate-50 hover:bg-slate-50/70 ${onSelectCliente ? "cursor-pointer" : ""}`}
                      onClick={() => onSelectCliente?.(c)}
                    >
                      <td className="px-3 py-2.5 font-medium text-slate-800">{c.clientName}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{c.maxDaysOverdue > 0 ? `${c.maxDaysOverdue} d` : "—"}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatCurrency(c.valorVencido)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(c.valorCarteira)}</td>
                      <td className="px-3 py-2.5 text-slate-600">{REGUA_LABEL[c.regua]}</td>
                      <td className="px-3 py-2.5">{SPC_LABEL[c.spcStatus]}</td>
                      <td className="px-3 py-2.5">{c.emAcordo ? "Em acordo" : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 text-xs text-slate-500">
          <p className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            {tab === "parcelas"
              ? `Saldo filtrado ${formatCurrency(saldoParcelas)}`
              : `${clientes.length} cliente(s)`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={safePage <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded-lg border border-slate-200 px-2 py-1 disabled:opacity-40"
            >
              Anterior
            </button>
            <span>
              {safePage + 1} / {pages}
            </span>
            <button
              type="button"
              disabled={safePage >= pages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-slate-200 px-2 py-1 disabled:opacity-40"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
